import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, X } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

/**
 * Reusable filter bar: a primary dimension Select + Month picker + Date picker + Clear.
 * Used on Productions (product), Milk Reception (supplier) and HACCP logs (CCP).
 * Phones: a 2-column grid with full-width controls; tablets/desktop: a wrapping row.
 */
export const ResultsFilter = ({
  selectLabel,
  selectValue,
  onSelectChange,
  selectOptions = [],
  allValue = "all",
  allLabel = "All",
  selectTestId,
  selectWidth = "sm:w-[180px]",
  monthValue,
  onMonthChange,
  monthTestId,
  dateValue,
  onDateChange,
  dateTestId,
  active,
  onClear,
  clearTestId,
  children,
}) => {
  const { t } = useT();
  return (
    <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-row sm:flex-wrap sm:items-end" data-testid="results-filter">
      <div className="col-span-2 space-y-1.5 sm:col-span-1">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> {selectLabel}
        </Label>
        <Select value={selectValue} onValueChange={onSelectChange}>
          <SelectTrigger className={`w-full ${selectWidth}`} data-testid={selectTestId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={allValue}>{allLabel}</SelectItem>
            {selectOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-0 space-y-1.5">
        <Label className="text-xs text-muted-foreground">{t("common.month")}</Label>
        <Input
          type="month"
          value={monthValue}
          onChange={(e) => onMonthChange(e.target.value)}
          className="w-full min-w-0 sm:w-[170px]"
          data-testid={monthTestId}
        />
      </div>
      <div className="min-w-0 space-y-1.5">
        <Label className="text-xs text-muted-foreground">{t("common.date")}</Label>
        <Input
          type="date"
          value={dateValue}
          onChange={(e) => onDateChange(e.target.value)}
          className="w-full min-w-0 sm:w-[170px]"
          data-testid={dateTestId}
        />
      </div>
      {children}
      {active && (
        <Button variant="ghost" size="sm" onClick={onClear} data-testid={clearTestId} className="col-span-2 justify-self-start sm:col-span-1">
          <X className="mr-1.5 h-4 w-4" /> {t("common.clear")}
        </Button>
      )}
    </div>
  );
};
